import { useId, type ReactElement } from "react";
import type { TagsParams, TagsTrend } from "@playbookai/widget-schemas";
import { Input } from "../../atoms/Input/Input.js";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import { NativeSelect } from "../../primitives/NativeSelect.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { TAGS_TREND_OPTIONS } from "./presentation.js";
import { TagsTrendPill } from "./TagsTrendPill.js";

const CARD_CLASS =
  "min-w-0 rounded-lg border border-pb-border bg-pb-card-elevated/60 p-4 md:p-5";
const LABEL_CLASS = "text-sm font-medium text-pb-text-muted";
const VALUE_CLASS =
  "whitespace-pre-wrap break-words text-base leading-relaxed text-pb-text [overflow-wrap:anywhere]";
const TREND_GUIDANCE =
  "Direction across comparable periods. Choose Not sure when it cannot be established.";

/** Read-only stand-in for a field nobody has filled in yet. */
const NOT_ESTABLISHED = (
  <span className="italic text-pb-text-muted">Not established</span>
);

interface TagsFieldCardProps {
  readonly label: string;
  readonly guidance?: string;
  readonly placeholder: string;
  readonly value: string;
  readonly readOnly: boolean;
  /** PB-4923 AC5 — the merge-map leaf this card edits. */
  readonly targetKey: string;
  readonly onValueChange: (next: string) => void;
}

/**
 * One labelled field card. Editable renders a multiline control named by a
 * real `<label>`; read-only renders presentation text inside a `group` named by
 * the same visible field name, so the value stays findable without a control.
 */
function TagsFieldCard({
  label,
  guidance,
  placeholder,
  value,
  readOnly,
  targetKey,
  onValueChange,
}: TagsFieldCardProps): ReactElement {
  const fieldId = useId();
  const guidanceId = `${fieldId}-guidance`;
  const lease = useFieldEditSession(targetKey);
  const heading = (
    <div className="flex items-center gap-2">
      {readOnly ? (
        <span id={fieldId} className={LABEL_CLASS}>
          {label}
        </span>
      ) : (
        <label htmlFor={fieldId} className={LABEL_CLASS}>
          {label}
        </label>
      )}
    </div>
  );
  const guidanceText = guidance ?? null;

  if (readOnly) {
    return (
      <div role="group" aria-labelledby={fieldId} className={CARD_CLASS}>
        {heading}
        {guidanceText === null ? null : (
          <p className="mt-1 text-dense-1 text-pb-text-muted">{guidanceText}</p>
        )}
        <p className={`mt-2 ${VALUE_CLASS}`}>
          {value.trim() === "" ? NOT_ESTABLISHED : value}
        </p>
      </div>
    );
  }

  return (
    <div className={CARD_CLASS}>
      {heading}
      {guidanceText === null ? null : (
        <p id={guidanceId} className="mt-1 text-dense-1 text-pb-text-muted">
          {guidanceText}
        </p>
      )}
      <Textarea
        id={fieldId}
        rows={3}
        maxLength={255}
        value={value}
        placeholder={placeholder}
        wrapperClassName="mt-2"
        {...(guidanceText === null ? {} : { "aria-describedby": guidanceId })}
        {...lease}
        onChange={(event) => onValueChange(event.target.value)}
      />
    </div>
  );
}

interface TagsTrendCardProps {
  readonly value: TagsTrend;
  readonly readOnly: boolean;
  readonly onValueChange: (next: TagsTrend) => void;
}

/**
 * Trend is a closed classification, so its card is a labelled native select
 * over the four canonical values rather than free text; read-only renders the
 * same tone + label pill the compact canvas card shows.
 */
function TagsTrendCard({
  value,
  readOnly,
  onValueChange,
}: TagsTrendCardProps): ReactElement {
  const fieldId = useId();
  const guidanceId = `${fieldId}-guidance`;
  /**
   * PB-4923 AC5 — a SELECT holds a claim too.
   *
   * Easy to skip on the reasoning that a dropdown commits instantly, so there
   * is no draft to lose. That is wrong twice: the person has the list open and
   * is deciding, which is the same window the lease exists for; and an
   * approved AI change landing on `trend` between opening the list and picking
   * would be overwritten by the pick with no sign either happened.
   */
  const lease = useFieldEditSession("trend");

  if (readOnly) {
    return (
      <div role="group" aria-labelledby={fieldId} className={CARD_CLASS}>
        <div className="flex items-center gap-2">
          <span id={fieldId} className={LABEL_CLASS}>
            Trend
          </span>
        </div>
        <p className="mt-1 text-dense-1 text-pb-text-muted">{TREND_GUIDANCE}</p>
        <p className="mt-2">
          <TagsTrendPill trend={value} />
        </p>
      </div>
    );
  }

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center gap-2">
        <label htmlFor={fieldId} className={LABEL_CLASS}>
          Trend
        </label>
      </div>
      <p id={guidanceId} className="mt-1 text-dense-1 text-pb-text-muted">
        {TREND_GUIDANCE}
      </p>
      <div className="mt-2 md:max-w-sm">
        <NativeSelect<TagsTrend>
          id={fieldId}
          aria-describedby={guidanceId}
          options={TAGS_TREND_OPTIONS}
          value={value}
          {...lease}
          onChange={onValueChange}
        />
      </div>
    </div>
  );
}

export function TagsExpandedEditor({
  data,
  onChange,
  readOnly,
  embedded = false,
}: WidgetBodyProps<TagsParams> & {
  /** Removes widget chrome when the editor is composed inside a labelled card. */
  readonly embedded?: boolean;
}): ReactElement {
  const isReadOnly = readOnly === true;
  const metricLabelId = useId();
  // PB-4923 AC5. Metric is the one leaf rendered inline here rather than
  // through `TagsFieldCard`, so it claims its own.
  const metricLease = useFieldEditSession("metric");

  const commit = (next: TagsParams): void => {
    if (isReadOnly) return;
    onChange(next);
  };

  return (
    <div
      className={`flex h-full min-h-0 flex-col gap-4 text-pb-text ${embedded ? "" : "p-5 md:p-8"}`}
    >
      {!embedded ? (
        <div className="mx-auto flex w-full max-w-5xl shrink-0">
          <p className="ms-auto text-end text-dense-1 text-pb-text-muted">
            {isReadOnly ? "Read-only" : "Changes save automatically"}
          </p>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto pe-1">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
          {isReadOnly ? (
            <div role="group" aria-labelledby={metricLabelId}>
              <span
                id={metricLabelId}
                className={`mb-1.5 block ${LABEL_CLASS}`}
              >
                Metric
              </span>
              <p className={`${VALUE_CLASS} font-semibold`}>
                {data.metric.trim() === "" ? NOT_ESTABLISHED : data.metric}
              </p>
            </div>
          ) : (
            <Input
              label="Metric"
              maxLength={255}
              value={data.metric}
              placeholder="Leak rate"
              {...metricLease}
              onChange={(event) =>
                commit({ ...data, metric: event.target.value })
              }
            />
          )}

          <TagsTrendCard
            value={data.trend}
            readOnly={isReadOnly}
            onValueChange={(next) => commit({ ...data, trend: next })}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TagsFieldCard
              label="Actual"
              placeholder="7–8%"
              value={data.actual}
              readOnly={isReadOnly}
              targetKey="actual"
              onValueChange={(next) => commit({ ...data, actual: next })}
            />
            <TagsFieldCard
              label="Standard"
              placeholder="≤ 5%"
              value={data.standard}
              readOnly={isReadOnly}
              targetKey="standard"
              onValueChange={(next) => commit({ ...data, standard: next })}
            />
          </div>

          <TagsFieldCard
            label="Gap"
            placeholder="2–3 percentage points above standard"
            value={data.gap}
            readOnly={isReadOnly}
            targetKey="gap"
            onValueChange={(next) => commit({ ...data, gap: next })}
          />
        </div>
      </div>
    </div>
  );
}
